import { useQuery } from "@tanstack/react-query";
import { listPlayerMail } from "@/lib/api";
import { HERO_NAMES, mailRewardTypeLabel } from "@/types/cloud-data";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";

interface MailTabProps {
  playfabId: string;
  onSendMail: () => void;
}

export function MailTab({ playfabId, onSendMail }: MailTabProps) {
  const query = useQuery({
    queryKey: ["player-mail", playfabId],
    queryFn: () => listPlayerMail(playfabId),
    enabled: !!playfabId,
  });

  if (query.isLoading) return <Skeleton className="h-40 w-full" />;
  if (query.isError) {
    return <div className="text-sm text-destructive">{String(query.error)}</div>;
  }

  const mails = query.data ?? [];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">
          {mails.length} mail message{mails.length === 1 ? "" : "s"}
        </span>
        <Button variant="secondary" size="sm" onClick={onSendMail}>Send mail</Button>
      </div>

      {mails.length === 0 ? (
        <div className="text-sm text-muted-foreground">No personal mail</div>
      ) : (
        <div className="space-y-3">
          {mails.map((mail) => (
            <div key={mail.id} className="rounded border border-border overflow-hidden">
              <div className="bg-muted/50 px-3 py-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
                <span className="font-medium text-foreground">{mail.subject || "(no subject)"}</span>
                <span className="text-muted-foreground font-mono">{mail.id.slice(0, 8)}…</span>
                <span className="text-muted-foreground">
                  {new Date(mail.createdAt).toLocaleString()}
                </span>
                <span className={mail.readAt ? "text-muted-foreground" : "text-amber-600"}>
                  {mail.readAt ? "Read" : "Unread"}
                </span>
                <span className={mail.claimedAt ? "text-muted-foreground" : "text-amber-600"}>
                  {mail.claimedAt
                    ? "Claimed"
                    : mail.rewards.length > 0
                      ? "Unclaimed"
                      : "No rewards"}
                </span>
              </div>
              {mail.body ? (
                <div className="px-3 py-2 text-xs whitespace-pre-wrap border-t border-border/50">
                  {mail.body}
                </div>
              ) : null}
              {mail.rewards.length > 0 ? (
                <table className="w-full text-sm border-t border-border/50">
                  <thead>
                    <tr className="text-xs text-muted-foreground">
                      <th className="px-3 py-1.5 text-left">Type</th>
                      <th className="px-3 py-1.5 text-left">Amount</th>
                      <th className="px-3 py-1.5 text-left">Item ID</th>
                      <th className="px-3 py-1.5 text-left">Hero</th>
                      <th className="px-3 py-1.5 text-left">Extra</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mail.rewards.map((r, i) => (
                      <tr key={i} className="border-t border-border/40">
                        <td className="px-3 py-1.5 text-xs font-mono">{mailRewardTypeLabel(r)}</td>
                        <td className="px-3 py-1.5 text-xs">{r.amount}</td>
                        <td className="px-3 py-1.5 text-xs font-mono">{r.itemID ?? "—"}</td>
                        <td className="px-3 py-1.5 text-xs font-mono">
                          {typeof r.hero === "number" ? HERO_NAMES[r.hero] ?? r.hero : "—"}
                        </td>
                        <td className="px-3 py-1.5 text-xs font-mono">
                          {r.developerBuffID ?? (r.recipe != null ? `recipe:${r.recipe}` : "—")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
